// ModelSelection.tsx

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import Image from "next/image";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IModelConfig, UserPremiumType } from "types";
import { Label } from "utils/styling/typography.styled";
import { BadgeContainer, CloseButton, DefaultModelContainer, HeaderContainer, LabelWrapper, ModelDescription, ModelDescriptionText, ModelHeader, ModelImage, ModelInfo, ModelName, ModelsGrid, ModelTitle, NewBadge, SearchContainer, SearchIcon, SearchInputContainer, StyledCard, StyledCardContent, StyledDialogContent, StyledDialogHeader, StyledDialogTitle, StyledInput, StyledScrollArea, SubBadge } from "./ModelSelection.styled";
interface ModelSelectionProps {
  modelConfigs: IModelConfig[];
  onConfigChange: (newConfig: any) => void;
  selectedModel: IModelConfig | null;
  setSelectedModel: (model: IModelConfig | null) => void;
}
const ModelSelection: React.FC<ModelSelectionProps> = ({
  modelConfigs,
  onConfigChange,
  selectedModel,
  setSelectedModel
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const isInitialConfigSet = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (modelConfigs.length > 0 && !isInitialConfigSet.current && !selectedModel) {
      const defaultModel = modelConfigs.find(model => model.modelName === "Flux.Dev") || modelConfigs[0];
      setSelectedModel(defaultModel);
      onConfigChange({
        model: defaultModel.modelName,
        cfg: defaultModel.defaultCfgScale.toString(),
        steps: defaultModel.defaultSteps.toString(),
        sampler: defaultModel.defaultSampler
      });
      isInitialConfigSet.current = true;
    }
  }, [modelConfigs, onConfigChange, selectedModel, setSelectedModel]);
  const filteredModels = useMemo(() => {
    return modelConfigs.filter(model => model.modelName.toLowerCase().includes(searchTerm.toLowerCase()) || model.description.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [modelConfigs, searchTerm]);
  const handleModelClick = (model: IModelConfig) => {
    if (model.subscriberOnly && state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setSelectedModel(model);
    onConfigChange({
      model: model.modelName,
      cfg: model.defaultCfgScale.toString(),
      steps: model.defaultSteps.toString(),
      sampler: model.defaultSampler
    });
    setModalIsOpen(false);
  };
  return <Dialog open={modalIsOpen} onOpenChange={setModalIsOpen} data-sentry-element="Dialog" data-sentry-component="ModelSelection" data-sentry-source-file="index.tsx">
            <div>
                <LabelWrapper data-sentry-element="LabelWrapper" data-sentry-source-file="index.tsx">
                    <Label data-sentry-element="Label" data-sentry-source-file="index.tsx">Select a Model</Label>
                </LabelWrapper>
                {selectedModel && <DialogTrigger asChild>
                        <DefaultModelContainer>
                            <Image src={selectedModel.thumbnailUrl} alt={selectedModel.modelName} width={50} height={50} priority />
                            <div>
                                <ModelName>{selectedModel.modelName}</ModelName>
                                <ModelDescription>
                                    {selectedModel.description}
                                </ModelDescription>
                            </div>
                        </DefaultModelContainer>
                    </DialogTrigger>}
                <StyledDialogContent data-sentry-element="StyledDialogContent" data-sentry-source-file="index.tsx">
                    <StyledDialogHeader data-sentry-element="StyledDialogHeader" data-sentry-source-file="index.tsx">
                        <HeaderContainer data-sentry-element="HeaderContainer" data-sentry-source-file="index.tsx">
                            <StyledDialogTitle data-sentry-element="StyledDialogTitle" data-sentry-source-file="index.tsx">
                                Select a Model
                            </StyledDialogTitle>
                            <CloseButton variant="ghost" onClick={() => setModalIsOpen(false)} data-sentry-element="CloseButton" data-sentry-source-file="index.tsx">
                                <X className="h-4 w-4" data-sentry-element="X" data-sentry-source-file="index.tsx" />
                                <span className="sr-only">Close</span>
                            </CloseButton>
                        </HeaderContainer>
                    </StyledDialogHeader>
                    <SearchContainer data-sentry-element="SearchContainer" data-sentry-source-file="index.tsx">
                        <SearchInputContainer data-sentry-element="SearchInputContainer" data-sentry-source-file="index.tsx">
                            <SearchIcon data-sentry-element="SearchIcon" data-sentry-source-file="index.tsx" />
                            <StyledInput placeholder="Search models..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-sentry-element="StyledInput" data-sentry-source-file="index.tsx" />
                        </SearchInputContainer>
                    </SearchContainer>
                    <StyledScrollArea data-sentry-element="StyledScrollArea" data-sentry-source-file="index.tsx">
                        <ModelsGrid data-sentry-element="ModelsGrid" data-sentry-source-file="index.tsx">
                            {filteredModels.map(model => <StyledCard key={model.modelName} onClick={() => handleModelClick(model)}>
                                    <StyledCardContent>
                                        <ModelImage src={model.thumbnailUrl} alt={`${model.modelName} preview`} width={60} height={60} />
                                        <ModelInfo>
                                            <ModelHeader>
                                                <ModelTitle>
                                                    {model.modelName}
                                                </ModelTitle>
                                                <BadgeContainer>
                                                    {(model.modelName === "Flux.Pro" || model.modelName === "OmniGen V1" || model.modelName === "Stable Diffusion 3.5 Large Base" || model.modelName === "Stable Diffusion 3.5 Large Turbo") && <NewBadge>New</NewBadge>}
                                                    {model.subscriberOnly && <SubBadge>Sub</SubBadge>}
                                                </BadgeContainer>
                                            </ModelHeader>
                                            <ModelDescriptionText>
                                                {model.description}
                                            </ModelDescriptionText>
                                        </ModelInfo>
                                    </StyledCardContent>
                                </StyledCard>)}
                        </ModelsGrid>
                    </StyledScrollArea>
                </StyledDialogContent>
            </div>
        </Dialog>;
};
export default ModelSelection;